<template>
  <transition name="fade" mode="out-in">
    <div class="col-span-full sm:col-span-full md:col-span-7">
      <h1 class="text-t2 mb-4">Tvättstuga och lokaler</h1>
      <div
        v-if="!isLoading && !!webapp_config.msg.laundry_description"
        class="mb-4 rounded-[15px] bg-primary-10 p-4"
        v-html="webapp_config.msg.laundry_description"
      ></div>

      <box class="mb-8">
        <h2 v-if="hasAnyBookableLocations && !isLoading" class="text-t4 mb-6">Boka tid</h2>

        <CardLoaderSmall v-if="isLoading || bookingableLaundryRooms.length<1" class="flex-1 flex-col !p-0 !ring-0" />

        <transition v-if="(bookingRules || webapp_config.msg.laundry_info) && selectedLaundryRoom" name="fade" mode="out-in">
          <div v-if="bookingRules" class="mb-4 rounded-[15px] bg-gray-10 p-4">
            <p>{{ bookingRules }}</p>
            <a v-if="bookingRulesLink" v-bind:href="bookingRulesLink" class="underline">Läs mer</a>
          </div>

          <div v-else class="mb-4 rounded-[15px] bg-gray-10 p-4">
            <p v-html="webapp_config.msg.laundry_info" />
          </div>
        </transition>

        <div v-if="hasValidApartmentContract">
            <SelectField
              v-if="ifMultipleContracts && !isLoading"
              id="id"
              class="md:max-w-md mb-4"
              label="Välj adress"
              placeholder=""
              :floatingLabel="true"
              :options="sortedApartments"
              v-model="contractsData"
              @change="onSelectedContract"
            />
          <div v-if="!isLoading && selectedContract && hasAnyBookableLocations && bookingCategories && bookingCategories.CategoryList.length>0 && bookingableLaundryRooms.length>0 && bookableRooms.length>0">
            <div class="mb-6 flex items-center flex-col md:flex-row justify-between gap-6 gap:0">
              <SelectField
                class="md:max-w-md"
                ref="roomSelector"
                id="title"
                placeholder=""
                label="Välj rum"
                :floatingLabel="true"
                :options="bookableRooms"
                v-model="selectedLaundryRoom"
                @change="onSelectLaundryRoom"
              />

              <div v-if="selectedLaundryRoom" class="flex items-center gap-4">
                <i
                  class="cursor-pointer text-dark-turquoise hover:bg-turquoise-10 hover:border-turquoise-40 focus:border-turquoise-60 border-transparent rounded-md h-8 w-8 flex items-center justify-center"
                  tabindex="0"
                  @click="removeWeek()"
                  @keyup.enter="removeWeek()"
                >
                  <IconChevronLeft />
                </i>
                <div class="text-t6" v-if="selectedCalendarDays.length > 0">
                  Vecka {{ getWeek(selectedCalendarDays[0].date) }},
                  {{ getYear(selectedCalendarDays[0].date) }}
                </div>
                <i
                  class="cursor-pointer text-dark-turquoise hover:bg-turquoise-10 hover:border-turquoise-40 focus:border-turquoise-60 border-transparent rounded-md h-8 w-8 flex items-center justify-center"
                  tabindex="0"
                  @click="addWeek()"
                  @keyup.enter="addWeek()"
                >
                  <IconChevronRight />
                </i>
              </div>
            </div>
            <div>
              <div>

                <transition v-if="selectedLaundryRoom" name="fade" mode="out-in">
                  <div>
                    <Loader v-if="showCalendarLoader && !isLoading" />
                    <div class="grid grid-cols-7 gap-1">
                      <template v-for="day in selectedCalendarDays">
                        <div class="text-center">
                          <p class="text-b2">{{ formatDate(day.date, 'ddd') }}</p>

                          <p class="text-b2 mb-4 font-thick">
                            {{ formatDate(day.date, 'D') }} {{ formatDate(day.date, 'MMM') }}
                          </p>

                          <template
                            v-for="(timeslot, index) in day.timeslots"
                            :key="timeslot.date + timeslot.startTime + timeslot.endTime"
                          >
                            <laundry-timeslot :timeslot="timeslot" :bookingRules="bookingRules" :bookingRulesLink="bookingRulesLink"></laundry-timeslot>
                          </template>
                        </div>
                      </template>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
          <div v-else-if="selectedContract && selectedContract.length > 4 && !isLoadingBookings && !hasAnyBookableLocations && !showLoader" class="">
            <strong>
              Du har tyvärr inte tillgång till någon tvättstuga som kan bokas via Mina sidor.
            </strong>
          </div>
        </div>
        <div v-else-if="!isLoading">
          <strong>Du har för närvarande inget hyresavtal hos Gavlegårdarna</strong>
        </div>

        <Loader v-if="loadingRooms && !isLoading" />
      </box>

        <Box v-if="(hasAnyBookableLocations)" class="mb-8">
        <h2 v-if="!isLoading" class="text-t4 mb-6">Mina bokningar</h2>
        <h3 v-if="!isLoading" class="text-t6 mb-4">Aktuella bokningar</h3>

        <CardLoaderSmall v-if="isLoading || isLoadingBookings" class="flex-1 flex-col !p-0 !ring-0" />

        <div v-else>
          <div
            v-if="bookingsFromToday.length > 0"
            v-for="booking in bookingsFromToday"
            :key="booking.BookingId"

          >
            <laundry-booking-tile v-bind:booking="booking"></laundry-booking-tile>
          </div>
          <div v-if="bookingsFromToday.length == 0">Du har inga aktuella bokningar</div>
        </div>


        <h3 v-if="!isLoading" class="text-t6 mb-4 mt-6">Tidigare bokningar denna månad</h3>


        <CardLoaderSmall v-if="isLoading || isLoadingBookings" class="flex-1 flex-col !p-0 !ring-0" />

        <div v-else>
          <div
            v-if="bookingsLastMonth.length > 0"
            v-for="booking in bookingsLastMonth"
            :key="booking.BookingId"
            class="relative rounded-[8px] px-3 md:px-4 py-4 ring-1 ring-gray-light bg-gray-light mb-4">

            <div class="flex items-center gap-9">
              <div>{{ booking.LocationName }} - {{ booking.GroupName }}</div>
              <div>
                {{ niceDateFormat(booking.StartTimeStamp) }}
                {{ formatTime(booking.StartTimeStamp, 0) }} -
                {{ formatTime(booking.StartTimeStamp, booking.LengthMinutes) }}
              </div>
            </div>
          </div>
          <div v-if="bookingsLastMonth.length == 0">Du har inga tidigare bokningar denna månad</div>
        </div>
      </Box>


    </div>
  </transition>
</template>

<script>
import { axios_legacy as axios } from '@/store/config';
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Modal from '@/components/ui/modal';
import IconChevronLeft from '@/components/icons/IconChevronLeft';
import IconChevronRight from '@/components/icons/IconChevronRight';
import Loader from '@/components/ui/loader';
import Box from '@/components/ui/box';
import GeneralMixin from '@/mixins/general';
import LaundryTimeslot from '@/pages/laundry/laundry-timeslot';
import LaundryBookingTile from '@/pages/laundry/laundry-booking-tile';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';
import SelectField from '@/components/ui/form/select';
import {filter} from "lodash";

export default {
  name: 'laundry',
  mixins: [GeneralMixin],
  components: {
    Box,
    SelectField,
    Modal,
    IconChevronRight,
    IconChevronLeft,
    LaundryTimeslot,
    LaundryBookingTile,
    Loader,
    CardLoaderSmall,
  },
  data() {
    return {
      //week : 0,
      isLoadingBookings: true,
      loadingRooms: false,
      selectedLaundryRoom: null,
      selectedContract: null,
      contractsData: null,
      showInfo: false,
      noLaundry: '',
      sortedApartments: [],
      ifMultipleContracts: false,
      showLoader: false,
      showCalendarLoader: false,
      bookingRules: '',
      bookingRulesLink: '',
      isLoading: true,
      // testAlwaysLoading: true,
      bookableRooms: [],
    };
  },
  computed: {
    ...mapGetters({
      bookingsOnSelectedGroup: 'bookingsOnSelectedGroup',
      bookingCategories: 'bookingCategories',
      bookings: 'bookings',
      bookingCalender: 'bookingCalender',
      bookingableLaundryRooms: 'bookingableLaundryRooms',
      bookingsFromToday: 'bookingsFromToday',
      bookingsLastMonth: 'bookingsLastMonth',
      selectedCalendarDays: 'selectedCalendarDays',
      selectedGroupRentalObjectCode: 'selectedGroupRentalObjectCode',
      webapp_config: 'webapp_config',
      authStatus: 'authStatus',
      hasAnyBookableLocations: 'hasAnyBookableLocations',
      getApartments: 'getApartments',
      hasApartment: 'hasApartment',
      hasValidApartmentContract: 'hasValidApartmentContract',
    }),
  },
  created() {
    // Skrolla alltid till toppen
    window.scroll(0, 0);

    this.showLoader = true;

    if (this.testAlwaysLoading) return;

    this.$store.cache
      .dispatch('fetchContracts')
      .then((contracts) => {
        if (!this.hasApartment) return;

        this.getApartments.forEach((el) => {
          const temp = {
            id: el.rentalObjectCode,
            label: el.rentalObject.address1,
            value: el.rentalObject,
          };

          if (el.status !== 'coming' && el.status !== 'ceased' && el.status !== 'awaitingSignature') {
            this.sortedApartments.push(temp);
            this.isLoading = true;

            this.$store.cache
              .dispatch('getBookingCategories', el.rentalObjectCode)
              .then(() => {
                this.$store.cache
                  .dispatch('getBookings', el.rentalObjectCode)
              }).finally(() => {
                this.isLoadingBookings = false;
                this.isLoading = false;

              if (this.sortedApartments.length === 1) {
                this.selectedContract = this.sortedApartments[0].id;
                this.setRooms(this.selectedContract);
                //this.getBookings(this.selectedContract);
              } else {
                this.ifMultipleContracts = true;
                this.$store.cache.dispatch('getConfig');
              }

            });
            }

        });

      })
      .finally(() => {
        this.showLoader = false;
        this.isLoading = false;
      });
  },


  watch: {},
  methods: {
    setRooms (id){
      this.bookableRooms = filter(this.bookingableLaundryRooms, (room) =>
        room.RentalObjectCode == id
      )
    },
    niceDateFormat(d) {
      return dayjs(d).format('ddd') + ' ' + dayjs(d).format('DD MMM');
    },
    formatTime(time, length) {
      return dayjs(time).add(length, 'minute').format('HH:mm');
    },
    getWeek(date) {
      return dayjs(date).week();
    },
    getYear(date) {
      return dayjs(date).year();
    },
    isToday(date) {
      return dayjs(date).isSame(new Date(), 'day');
    },
    onSelectedContract(contract) {
      this.selectedContract = contract;
      this.selectedLaundryRoom = null;
      this.noLaundry = '';
      this.setRooms(contract.id);
      /*this.showLoader = true;
      this.loadingRooms = true;*/
      /*return this.getBookings(contract.id).finally(() => {
        this.showLoader = false;
        this.loadingRooms = false;
      });*/
    },
    closeModal() {
      document.removeEventListener('keyup', this._keyUpListeners);
      document.querySelector('html').classList.remove('modal-open');
      this.showInfo = false;
    },
    getBookings(contractID) {
      this.isLoadingBookings = true;
      return this.$store.dispatch('getBookingCategories', contractID)
        .then(categoriesResult => {
          if (categoriesResult.status === 'rejected') {
            console.error('Failed to get booking categories:', categoriesResult.reason);
            return;
          }

          return this.$store.dispatch('getBookings', contractID);
        })
        .then(bookingsResult => {
          if (bookingsResult && bookingsResult.status === 'rejected') {
            console.error('Failed to get bookings:', bookingsResult.reason);
          }
        })
        .finally(() => {
          this.isLoadingBookings = false;
          if (!this.hasAnyBookableLocations) {
            this.noLaundry = 'Denna adress har inte någon tvättstuga som är bokningsbar digitalt';
          }
        });
    },

    onSelectLaundryRoom(laundryRoom) {
      this.selectedLaundryRoom = laundryRoom;

      this.showLoader = true;
      this.loadingRooms = true;
      this.$store.dispatch('setGroupId', laundryRoom.id);
      this.$store.dispatch('setRentalObjectCode', laundryRoom.RentalObjectCode);

      this.getBookingRules(laundryRoom.id);
//      this.$store.dispatch('getBookings', this.selectedContract.id);

      //  this.$store.dispatch('getBookings')
      // return this.$store.cache.dispatch('getBookingCalender',{ RentalObjectCode : 'rentr', groupId : 'grp2'})
      return this.$store.dispatch('getBookingCalender').finally(() => {
        this.showLoader = false;
        this.loadingRooms = false;
      });
    },

    addWeek() {
      this.showCalendarLoader = true;

      return this.$store.dispatch('increaseWeek').finally(() => {
        this.showCalendarLoader = false;
      });
    },

    removeWeek() {
      this.showCalendarLoader = true;
      return this.$store.dispatch('decreaseWeek').finally(() => {
        this.showCalendarLoader = false;
      });
    },

    getBookingRules(id) {
      this.bookingRules = null;
      this.bookingRulesLink = null;

      axios.get('https://www.gavlegardarna.se/index.php?rest_route=/api/v1/booking-rules/' + id + '/').then(
        function (response) {
          //self.$http.get('http://www.gavlegardarna.se.test:8500/index.php?rest_route=/api/v1/booking-rules/'+id+'/').then(function(response)  {
          //console.log(response.body);
          if (response && response.body) {
            this.bookingRules = response?.body?.short ?? '';
            this.bookingRulesLink = response?.body?.url ?? '';
          }
        },
        function (response) {
          console.log('Error');
        },
      );
    },

    /**
     * Refreshar listan över mina kontrakt.
     */
    updateListOfContracts() {
      this.showLoader = true;
      this.$store.cache.delete('fetchContracts');

      return this.$store.cache.dispatch('fetchContracts').finally(() => {
        this.showLoader = false;
      });
    },
  },
};
</script>

